/* EVERYTHING THAT TOUCHES THE DISK.
 *
 * The third cut out of `src/game.ts`, after `src/audio.ts` and `src/hud.ts`.
 * A robustness review scored `structure` 6 out of 10 on what was left — 4,600
 * lines, 93.7% of them one class — and named this seam: the save format, the
 * key grammar, the loader's clamps and the records store are read and written
 * by nothing else in the game, and every one of them exists because a run was
 * lost once and the comment above it says how.
 *
 * THIS IS A MOVE, NOT A REDESIGN. The format is unchanged to the byte, the
 * order of operations inside each function is unchanged, and every comment
 * came across with the code it explains. `Game` keeps a one-line delegator for
 * each entry point — `Game.peekSave`, `game.save()`, `Game.records()` — so
 * `src/main.ts`, `src/hud.ts` and the suites call exactly what they called
 * before.
 *
 * TWO THINGS TOOK AN ARGUMENT INSTEAD OF AN IMPORT, because `game.ts` imports
 * `world.ts` and `art.ts` and a module that imported `game.ts`
 * back would close a cycle:
 *
 *   `game`  — the live session, for the functions that were instance methods.
 *             Same shape as `src/audio.ts` and `src/hud.ts` use.
 *   `host`  — whoever owns the records parse cache. `Game` owns the field, and
 *             the suite clears it with `(Game as any).recCache = null`, so the
 *             reader is handed the holder rather than keeping a cache of its
 *             own. A second cache in here would be a second answer to "is the
 *             store stale", which is this project's oldest recurring defect.
 *
 * The ceilings the loader clamps to moved to `src/rules.ts`, which exists for
 * exactly this: a constant two modules must agree on living somewhere neither
 * of them owns. They are re-exported from `game.ts`, where the suite imports
 * them from.
 */
import { T } from './art.ts';
import { WORLD_W, WORLD_H, COLS, ROWS } from './world.ts';
import { migrate, sanitiseBag, sanitiseGear, ITEMS } from './sim/inventory.ts';
import { PW, PH, ENEMY_DEFS, Item } from './entities.ts';
import { BEAST_GROWTH } from './rules.ts';
import { afflict, AFFLICTION_NAMES } from './sim/afflictions.ts';   // what ails a body is saved with it (2026-09-17)
import {
  EMBER_COOL, MAX_HP_CAP, MAX_PODS_CAP, MAX_HULLS, TOOLS,
} from './rules.ts';

/* THE ONE PLACE THAT KNOWS WHAT A SAVED KEY LOOKS LIKE.
 *
 * Round 29 hardened `broken` and `quiet` against a crafted save that could
 * inflate a slot past a browser quota — and wrote a fourth, independent model
 * of the area key to do it, buried inside `loadSave`. It accepted
 * `^([WH])(\d+)?\|?(-?\d+),(-?\d+)$`, and the game writes FIVE shapes:
 *
 *     W|3,4      wild      broken   accepted
 *     H|1,3,4    hollow    broken   REJECTED — three numbers, the regex ends at two
 *     C|3,4      cave      broken   REJECTED — `C` is not in [WH]
 *     3,4        wild      quiet    accepted
 *     h1:3,4     hollow    quiet    REJECTED — lowercase h, a colon
 *
 * Measured end to end: a tile blasted inside a hollow was standing again
 * after a reload, and the anti-farm latch reset, turning twelve room entries
 * from 2 spawned enemies into 24. That is the "a reload is a refill" exploit
 * this file's own comment says was closed, and progress loss — the project's
 * one absolute rule — introduced by the fix for a previous progress loss.
 *
 * `src/passable.ts` opens by describing exactly this failure ("fifteen sites,
 * six of them independent models") and centralising it. So: one grammar, and
 * `areaKey`/`quietKey` are checked against it by the suite, which makes the
 * regression unrepresentable rather than merely fixed.
 *
 * The digit caps are load-bearing too — a key of 20,000 digits passed the old
 * filter, and 512 of them wrote 10 MB back out of a save the loader accepted.
 */
export const AREA_KEY_RE = /^(?:W\|-?\d{1,3},-?\d{1,3}|H\|\d{1,2},-?\d{1,3},-?\d{1,3}|C\|-?\d{1,3},-?\d{1,3})$/;
export const QUIET_KEY_RE = /^(?:h\d{1,2}:-?\d{1,3},-?\d{1,3}|-?\d{1,3},-?\d{1,3})$/;

/** ONE ANSWER TO "IS THIS A SCREEN COORDINATE, AND WHICH ONE".
 *
 * There were four copies of this filter in `loadSave` — two of them
 * byte-identical — and every one of them validated the DECODED number while
 * storing the RAW string. `Number('0003') === 3`, so `'0003,4'` passed and was
 * kept as `'0003,4'`: a Set member distinct from `'3,4'` that means the same
 * screen. Seventeen spellings of tile (3,4) were accepted in one save —
 * `'0x3,4'`, `'3e0,4'`, `' 3,4'`, `'0b11,4'`, unbounded leading zeros — and
 * with no size bound on `visited` or `charted`, 20,000 of them wrote 6.5 MB,
 * past the localStorage quota on every mainstream browser. Which latches
 * `storageFailed` for that seed, and it clears only on a good write that can
 * now never happen.
 *
 * This returns the CANONICAL key or null, so aliases collapse to one member
 * and the collections can be bounded by a count that means something.
 */
export function coordKey(k, w, h) {
  if (typeof k !== 'string' || k.length > 16) return null;
  const parts = k.split(',');
  if (parts.length !== 2) return null;
  const x = Number(parts[0]), y = Number(parts[1]);
  if (!Number.isInteger(x) || !Number.isInteger(y)) return null;
  if (x < 0 || y < 0 || x >= w || y >= h) return null;
  return x + ',' + y;
}

/* WHAT A RECORD IS.
 *
 * Written down because the shape was previously implied by `{}` — and `{}`
 * is the type that accepts everything and permits nothing, which is why
 * every read of `.wins` or `.best` was an error. This is also the store a
 * robustness review found unbounded and unvalidated (see the board), so
 * naming the shape is the first half of bounding it. */

/** One seed's best run. `at` is wall-clock, which is why a save blob is not a
 *  pure function of play — see the determinism note in docs/REVIEW.md. */
export interface RunRecord {
  frames?: number;
  glim?: number;
  kills?: number;
  changed?: number;   // the record screen's sum: what the run changed (round 52)
  felled?: number;
  at?: number;
}

/** Lifetime totals across every seed. */
export interface Totals {
  wins?: number;     // the wilds quieted: all three beasts down
  runs?: number;     // runs left by a stone (round 52)
  most?: number;     // the most a run changed
  kills?: number;
  seeds?: number;
  best?: number;
  felled?: Record<string, number>;   // THE STONES REMEMBER (LS-294, decided 2026-09-13): how many times each beast kind has fallen, over every run
}

/** Where the records parse cache lives. `Game` owns the field — the suite
 *  reaches in and clears it — so the reader below is handed the holder rather
 *  than keeping a cache of its own. */
export interface RecordHost {
  recCache: { raw: string; value: { seeds: Record<string, RunRecord>; total: Totals } } | null;
}

// One save PER SEED, plus a pointer to the most recent. Starting a new world
// used to overwrite the single save slot, so one stray press destroyed a run
// with no way back. Nothing here ever deletes another seed's progress.
const SAVE_PREFIX = 'lostseeds.run.v3.';
export const RECORD_KEY = 'lostseeds.records.v1';   // exported for the checks' reset (record.ts cleared 'rako.records' for a day and reset nothing, 2026-09-13)
/* WHAT BOUNDS THE ONE UNBOUNDED STORE.
 *
 * `records` was the only player-visible store with no cap on its size, no
 * limit on how many keys it held and no check on the shape of a value. A
 * robustness review poisoned it with 60,000 seed keys — 4,537,839 bytes — and
 * measured 34.7ms per `records()` call against `drawTitle` calling it TWICE
 * a frame: 69.4ms on a 16.7ms budget, roughly 14fps from the first frame the
 * title is up.
 *
 * The second half is worse than the first. That blob is 4.5MB of a 5MB origin
 * quota, spent before any run's `save()` asks for space — and a `save()` that
 * cannot get space is the one thing this project treats as unforgivable. So
 * the trade here is explicit: a leaderboard row is a nicety and a save is
 * not, and past the cap the rows are the thing that goes.
 *
 * 500 seeds is far past what play produces — it is one row per distinct seed
 * ever finished — and holds the blob near 50KB, where the parse is under a
 * tenth of a millisecond and can be afforded twice a frame without a cache.
 * There is a cache anyway, because a store this hot should not be re-parsed
 * for two identical reads in the same frame. */
const RECORD_MAX_SEEDS = 500;
/** A seed is any trimmed, lowercased string, so there is no grammar to check
 *  — but there is a length. Nothing the game generates comes near this. */
const RECORD_MAX_KEY = 64;
/** Past this the store is presumed poisoned rather than played: parse it once,
 *  prune it, and write the small version back so the cost is paid once ever. */
const RECORD_MAX_BYTES = 256 * 1024;
/** Freeze a records payload through its rows, so a caller that writes to what
 *  `records()` handed back fails loudly instead of poisoning the cache. */
function deepFreeze(rec: { seeds: Record<string, RunRecord>; total: Totals }) {
  for (const k of Object.keys(rec.seeds)) Object.freeze(rec.seeds[k]);
  Object.freeze(rec.seeds);
  Object.freeze(rec.total);
  return Object.freeze(rec);
}

const LAST_KEY = 'lostseeds.last.v3';
/** Normalised before it is encoded, so `Alpha`, `ALPHA` and `" alpha "` cannot
 *  become three save slots for what is meant to be one world. */
export const normaliseSeed = (seed) => String(seed == null ? '' : seed).trim().toLowerCase();
const saveKeyFor = (seed) => SAVE_PREFIX + encodeURIComponent(normaliseSeed(seed));
/** Quarantine slots live under their own prefix, not a suffix a seed can spell. */
const BAD_PREFIX = 'ls.bad!';
/** The one rule for whether a save can be loaded. */
export function looksSane(d) {
  if (!d || typeof d !== 'object' || d.v !== 2) return false;
  if (typeof d.seed !== 'string' || !d.seed) return false;
  /* `sane()` caps every string inside `state` at 64 chars; `d.seed` is not
   * inside `state`, so nothing saw it. A 3,000,000-char seed round-tripped
   * and wrote a 3,000,017-char KEY. Refusing routes it to the quarantine
   * path, which preserves the bytes rather than destroying them. Safe:
   * `cleanSeed` caps the base at SEED_MAX 24 and the canonical suffix tail
   * at 13, so no seed the game can spell comes near this. */
  if (d.seed.length > 64) return false;
  const a = d.area;
  if (!a || typeof a !== 'object') return false;
  // ANY KIND LOADS. A save left in a hollow or a cave before LS-226 names a
  // kind the game no longer has; it is not rubbish, it is a run, and the
  // loader wakes it in the wilds at the start rather than refusing it.
  if (typeof a.kind !== 'string' || a.kind.length > 16) return false;
  if (!Number.isInteger(a.x) || !Number.isInteger(a.y)) return false;
  if (a.kind === 'wild' && (a.x < 0 || a.y < 0 || a.x >= WORLD_W || a.y >= WORLD_H)) return false;
  if (d.player) {
    const pl = d.player;
    if (!Number.isFinite(pl.x) || !Number.isFinite(pl.y)) return false;
    if (!['up', 'down', 'left', 'right'].includes(pl.dir)) return false;
  }
  if (!d.state || typeof d.state !== 'object') return false;
  for (const k of ['hp', 'maxHp', 'glim', 'pods', 'maxPods', 'seeds', 'kills']) {
    if (!Number.isFinite(d.state[k])) return false;
  }
  return true;
}

// NB: every read, write and delete of a save must go through saveKeyFor. Two
// of the three used to build the key inline, so forgetRun('Mixed Case') left
// the slot sitting there.

export function save(game) {
  try {
    /* A CHECKPOINT IS A PLACE YOU CAN COME BACK TO (2026-09-18). Nothing stopped a save being written while the body
     * was falling, so a death that happened to coincide with an autosave wrote hp 0 as the seed's checkpoint -- and
     * a run loaded at nought vigor cannot be hurt and cannot die, because `Player.hurt` returns at once (the load
     * lifts such a slot to one point now, which is what made this visible: load-then-save produced a different file
     * and fuzz.ts asks about exactly that). The fall is not a checkpoint; the run that comes back from it is, and
     * `retry` saves once it has. */
    if (game.mode === 'dying' || game.mode === 'gameover') return;
    if (game.untouched) return;   // a resumed tab nobody has touched is not playing: it writes nothing, or it takes the run's ownership from the tab that is (2026-09-19)
    // A second tab on the same seed used to overwrite the first without a
    // word: both boot from LAST_KEY, both write the same key, last writer
    // wins. Measured, a tab left on the title screen wrote 0 glim over a
    // four-hundred-glim run. Refuse to write over a save that has moved on
    // since the one this session loaded, and say so on the HUD.
    const disk = peekSave(game.seed);
    // A slot we could not have loaded is not another tab's run, it is
    // rubbish, and refusing to replace it would strand the seed forever.
    const loadable = disk && game.saveLooksSane(disk);
    // -1, not 0, and never `undefined`. A session that has neither loaded
    // this save nor written it knows nothing about it, and must not
    // overwrite it — which is exactly the tab left sitting on a #seed URL
    // while another plays. Comparing against a bare `this.saveSeq` made
    // `N > undefined` false for every N and destroyed 200 of 200 runs.
    const mine = Number.isFinite(game.saveSeq) ? game.saveSeq : -1;
    if (loadable && Number.isFinite(disk.writeSeq) && disk.writeSeq > mine) {
      game.otherTab = true;
      return;
    }
    game.saveSeq = (game.saveSeq || 0) + 1;
    game.owned.set(game.seed, game.saveSeq);
    localStorage.setItem(saveKeyFor(game.seed), JSON.stringify({
      v: 2, seed: game.seed, writeSeq: game.saveSeq,
      /* AND A CHECKPOINT HAS A POINT OF VIGOR IN IT (2026-09-18). The load lifts a nought-vigor slot to one, because
       * a run loaded there can neither be hurt nor die; writing one is the other half of the same rule, so that
       * loading a save and saving it again gives the same file (fuzz.ts asks exactly that). The game's own invariant
       * is that nought vigor means a body falling, and a falling run writes nothing at all -- this is for a state no
       * ordinary play can reach, and it costs nothing when hp is honest. */
      state: game.state && Number.isFinite(game.state.hp) && game.state.hp <= 0 ? { ...game.state, hp: 1 } : game.state,
      area: game.area,
      visited: [...game.visited],
      // The respawn budget, or a reload is a refill: measured 11.4 -> 22.1
      // glim over twenty game-minutes with a reload every twenty seconds.
      quiet: [...game.quiet],
      charted: [...game.charted],
      broken: [...game.broken].map(([k, s]) => [k, [...s]]),
      placed: [...(game.placed || [])].map(([k, m]) => [k, [...m]]),   // what the player set down (LS-379)
      sim: game.systems ? game.systems.serialize(game) : null,   // the game, so the fires on the screen underfoot are stashed with the rest (2026-09-18)
      /* `space` SAYS WHICH COORDINATES THESE ARE, and it exists because they
       * changed. The wilds used to be screen-local — x ran 0 to 256 inside
       * whichever screen was current — and they are world coordinates now,
       * across a map 128x96 tiles across. A save written before the change
       * holds 0..256 and a save written after holds 0..2048, and the two are
       * indistinguishable without saying so. Restoring one as the other puts
       * the player on the wrong screen, which is losing a run. */
      player: { x: game.player.x, y: game.player.y, dir: game.player.dir, space: 'world' },
      /* THE RIDE IS PART OF WHERE YOU ARE (2026-09-17). Crossing a screen autosaves, so sailing saves mid-voyage --
       * and nothing on disk said the body was aboard, so the load found a body standing in open sea, called it
       * impossible, and rescued it to the nearest standable tile: another island, fifty tiles from its own hull. */
      aboard: !!game.aboard,
      /* AND WHAT AILS YOU IS AS REAL AS WHAT HELPS YOU (2026-09-17). Every timed BOON lives in `state` and was
       * saved to the frame; every HARM lives on the body, which the save wrote as four fields. A tusk wound bleeds
       * "until sap" -- seven vigor and an item -- and reopening the tab cured it for nothing, on a game that
       * autosaves at every screen. Bounded on the way in and again on the way out. */
      ail: [...(game.player && game.player.afflictions ? game.player.afflictions : [])]
        .filter(([, a]: any) => a && a.left > 0).slice(0, 16)
        .map(([n, a]: any) => [n, Math.round(a.left), Math.round(a.t || 0), Math.round(a.bit || 0)]),   // ...AND THE BEAT IT IS ON (2026-09-18): `t` is the clock and `bit` is how many bites it has already taken, and only the clock was written -- so a reload charged the run for every point the affliction had already collected
      chops: [...(game.treeChops || [])].slice(0, 256).map(([k, n]: any) => [k, Math.round(n)]),
      /* WHAT THE PLAYER PUT DOWN (LS-284). `items` was not serialised at all,
       * so taking a spear out of a full bag to make room and then reloading
       * lost it for ever -- and if it was beast gear, its hoard key was
       * already retired in `lootTaken`, so the world could not make another.
       * Only the player's own drops: the world's loot is regenerated. */
      /* ...AND THE STASH, WHICH IS WHERE MOST OF THEM ARE. `updateArea` keeps
       * the things inside the window and files everything else under the
       * screen it was left on, so a thing you dropped and walked away from is
       * not in `items` at all. Saving only `items` meant the fix worked
       * exactly as long as you stood next to what you had put down. */
      dropped: [...(game.items || []), ...(game.itemStash ? [...game.itemStash.values()].flat() : [])]
        .filter((i) => i && i.dropped && !i.dead).slice(0, 200)
        .map((i) => ({ kind: i.kind, x: Math.round(i.x), y: Math.round(i.y), dur: i.dur, ...((i as any).offered ? { offW: [(i as any).forgeTx, (i as any).forgeTy] } : {}), ...((i as any).seeded ? { seeded: 1 } : {}) })),   // AND WHAT LIES ON A FORGE'S ROCK IS THE FIRE'S (2026-09-17): an offering carries the rock it was laid on, so a reload hands the pile back to the forge instead of deleting it
      runFrames: game.runFrames,
      at: Date.now(),
    }));
    localStorage.setItem(LAST_KEY, game.seed);
    game.storageFailed = false;
    // Cleared on every good write. It used to latch forever: it survived
    // reset(), survived switching to a brand-new seed, and left the HUD
    // reading ANOTHER TAB OWNS THIS RUN over a run nothing else had touched.
    game.otherTab = false;
  } catch (err) {
    // Quota, private mode, blocked storage. Tell the player once: silently
    // not saving is how a run disappears.
    // Tell the player and stop. Deleting other saves to make room destroyed
    // four of six stored runs in twelve failed writes — far worse than the
    // problem it was added to solve. The run continues in memory.
    if (!game.storageFailed) {
      game.storageFailed = true;   // also draws a standing HUD line
      game.say('progress is not saving');
    }
  }
}


/** ...AND THE KEY AND THE PAYLOAD HAVE TO AGREE. Three callers already ask
 *  whether a slot's payload names the seed they asked for — `canLoad`
 *  (`d.seed === seed`), `listRuns` (`k !== saveKeyFor(d.seed)`), `retry`
 *  (`saved.seed === this.seed`) — and the two boot paths, which are the ones
 *  that actually start a session, asked nothing. A slot at key A whose
 *  payload says B was refused by the picker, refused by `canLoad`, and
 *  LOADED by `boot()`, after which the session autosaved into B's slot:
 *  measured, 40 of 40 trials replaced a 400-glim run with a 1-glim one. It
 *  needs a mismatched slot to happen (tamper, a partial rename, a future
 *  migration) and its consequence is a destroyed run, which is the one
 *  outcome this project treats as unacceptable. Four models, one answer now.
 *  A mismatched slot is returned to nobody, so `hasSaveBytes` still sees the
 *  bytes and the quarantine path sets them aside rather than overwriting. */
/** `seed` is optional: with none it reads the slot the session is on. */
export function peekSave(seed?) {
  try {
    const want = seed || localStorage.getItem(LAST_KEY);
    if (!want) return null;
    const raw = localStorage.getItem(saveKeyFor(want));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    // `normaliseSeed` on both sides: the key is built from the normalised
    // form, so comparing a raw payload seed against a raw argument would
    // reject a slot that is perfectly consistent.
    if (typeof data.seed !== 'string'
      || normaliseSeed(data.seed) !== normaliseSeed(want)) return null;
    return data;
  } catch (err) { return null; }
}

/** Set an unreadable slot aside instead of letting the next write destroy
 *  it. Booting on a save the loader refuses used to fall straight through to
 *  `reset(seed)`, and `save()`'s own overwrite guard only protects a slot
 *  that PASSES `looksSane` — which is exactly false for the one that just
 *  failed. Measured: 40 of 40 trials destroyed an 18,466-frame run. The live
 *  case is a format bump: the day `v` becomes 3, every save on disk is a
 *  save the loader refuses.
 *  @returns {boolean} whether anything was set aside. */
export function quarantineSave(seed) {
  try {
    const want = seed || localStorage.getItem(LAST_KEY);
    if (!want) return false;
    const key = saveKeyFor(want);
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    // THE LONGEST RUN WINS, NOT THE NEWEST. "Newest wins" assumed a second
    // failed boot would find nothing to set aside — but the slot is
    // rewritten by the fresh run's first autosave, so boot 2 quarantined ten
    // seconds of play over an 18,466-frame run, 40 of 40 trials. A
    // quarantine that can be overwritten by the accident that follows it is
    // not a quarantine. Compare what is already there and keep the longer.
    // A KEY THAT CANNOT COLLIDE WITH A REAL ONE. `saveKeyFor('alpha') +
    // '.bad'` is byte-identical to `saveKeyFor('alpha.bad')`, and `.` is in
    // the seed charset — so quarantining a damaged `alpha` either DELETED it
    // outright (the keep-the-longer branch, comparing against a healthy
    // `alpha.bad` run) or overwrote that healthy run with the damaged blob,
    // 40 of 40 trials each way. The prefix goes on the front, where no
    // encoded seed can reach it.
    const bad = BAD_PREFIX + key;
    const framesOf = (text) => {
      try { const d = JSON.parse(text); return Number.isFinite(d.runFrames) ? d.runFrames : -1; }
      catch (err) { return -1; }
    };
    const held = localStorage.getItem(bad);
    if (held !== null && framesOf(held) > framesOf(raw)) {
      // the older quarantine is worth more; drop the slot rather than it
      localStorage.removeItem(key);
      return true;
    }
    localStorage.setItem(bad, raw);
    // ...and only clear the slot once the copy is actually on disk. If
    // `setItem` throws on quota, removing the original would destroy the run
    // in the name of saving it.
    if (localStorage.getItem(bad) !== raw) return false;
    localStorage.removeItem(key);
    return true;
  } catch (err) { return false; }
}

/** Is there a save here at all, readable or not? `peekSave` returns null
 *  both when the slot is empty and when its bytes will not parse, so a
 *  caller that asks it cannot tell "nothing to lose" from "something we
 *  cannot read" — and the truncated case is exactly the one that needs
 *  setting aside. Measured: a damaged save was never quarantined, 40 of 40,
 *  and the next autosave wrote over it. */
export function hasSaveBytes(seed) {
  try {
    const want = seed || localStorage.getItem(LAST_KEY);
    return !!want && localStorage.getItem(saveKeyFor(want)) !== null;
  } catch (err) { return false; }
}

/** Has this player finished a run? The night is the only thing in the game
 *  that is not available on the first one. */
/** Has this player earned the right to be TOLD about the night?
 *
 *  IT GATES THE SIGNPOST, NOT THE MODE, and that is deliberate. An examiner
 *  read it as dead — "nightUnlocked() gates nothing; a first-run player
 *  typing #seed!night gets the night mode the title advertises as earned" —
 *  which is half a measurement: the title does not advertise it to a
 *  first-run player, because this is what decides whether that line is
 *  drawn at all. Measured on a fresh install the hint is hidden, and after
 *  one win it appears.
 *
 *  The mode itself stays open on purpose. It is a suffix you can type, it
 *  is in the README and the manual, and locking a URL a player has read
 *  about would make the game argue with its own documentation. Earning it
 *  buys the game telling you; it was never a key. */
export function nightUnlocked(host: RecordHost) {
  const rec = records(host);
  return !!(rec.total && (rec.total.wins > 0 || rec.total.runs > 0));   // a run left counts as a run had
}

export function records(host: RecordHost): { seeds: Record<string, RunRecord>; total: Totals } {
  const blank: { seeds: Record<string, RunRecord>; total: Totals } =
    { seeds: {}, total: {} as Totals };
  try {
    const raw = localStorage.getItem(RECORD_KEY);
    if (!raw) { host.recCache = null; return blank; }
    /* Two reads of the same bytes in one frame parse once. Keyed on the raw
     * string, so another tab writing the store still invalidates it — a
     * dirty flag set by our own writer would not. */
    const hit = host.recCache;
    if (hit && hit.raw === raw) return hit.value;
    const d = JSON.parse(raw);
    if (!d || typeof d !== 'object' || Array.isArray(d)) return blank;
    const out: { seeds: Record<string, RunRecord>; total: Totals } = { seeds: {}, total: {} as Totals };
    if (d.seeds && typeof d.seeds === 'object' && !Array.isArray(d.seeds)) {
      for (const [k, v] of Object.entries(d.seeds as Record<string, any>)) {
        if (k.length > RECORD_MAX_KEY) continue;
        // a record whose frames are not a number rendered as BEST NaN:NaN
        if (!v || typeof v !== 'object' || !Number.isFinite(v.frames)) continue;
        /* COPY THE FOUR FIELDS, DO NOT KEEP THE OBJECT. Validating `frames`
         * and then storing `v` whole let a row carry a megabyte of anything
         * else beside it, and hand it straight back out to the HUD. */
        /* FINITE IS NOT THE SAME AS SENSIBLE.
         *
         * This validated `Number.isFinite` and nothing else, so a forged
         * blob with `frames: -99999` and
         * `total: {wins: 1e9, kills: 1e9, seeds: 1e9, best: -1}` survived
         * intact, reached the HUD and made `nightUnlocked()` true. A
         * negative best is not a time. Clamped the way `sanitiseState`
         * clamps state, because "the one store with no range validation" is
         * how this one was described and it was the only one left. */
        if (v.frames <= 0) continue;
        const rec: RunRecord = { frames: Math.min(v.frames, 1e9) };
        for (const f of ['glim', 'kills', 'changed', 'felled'] as const)
          if (Number.isFinite(v[f]) && v[f] >= 0) rec[f] = Math.min(v[f], 1e12);
        /* A TIMESTAMP IS NOT A COUNT (2026-09-18). `at` is a millisecond clock -- 1.79e12 today -- and it was
         * clamped with the four tallies at 1e12, which is September 2001: every row on disk came back carrying the
         * same instant, and `prune()`'s rule of keeping the most recent rows could no longer tell this morning's run
         * from one in April. Bounded on its own scale, which is the largest instant a Date can hold. */
        if (Number.isFinite(v.at) && v.at >= 0) rec.at = Math.min(v.at, 8.64e15);
        out.seeds[k] = rec;
      }
    }
    if (d.total && typeof d.total === 'object' && !Array.isArray(d.total)) {
      for (const k of ['wins', 'runs', 'most', 'kills', 'seeds', 'best']) {
        const n = d.total[k];
        if (!Number.isFinite(n) || n < 0) continue;
        // a best of 0 frames is not a time either; wins/kills/seeds are counts
        if (k === 'best' && n <= 0) continue;
        out.total[k] = Math.min(Math.floor(n), 1e9);
      }
      // the beasts felled over every run: a count per kind, kinds kept to short names
      const f = d.total.felled;
      if (f && typeof f === 'object' && !Array.isArray(f)) { const kept: Record<string, number> = {}; for (const k of Object.keys(f)) { const n = f[k]; if (typeof k === 'string' && k.length <= 24 && Number.isFinite(n) && n > 0) kept[k] = Math.min(Math.floor(n), 1e9); } if (Object.keys(kept).length) out.total.felled = kept; }
    }
    const keys = Object.keys(out.seeds);
    if (keys.length > RECORD_MAX_SEEDS) out.seeds = prune(out.seeds);
    /* Self-heal, once. A store this big is presumed poisoned, and leaving it
     * on disk means paying the 34.7ms parse again on the next cold load —
     * and leaving 4.5MB of the quota unavailable to `save()`, which is the
     * part that actually costs a player something. */
    let key = raw;
    if (raw.length > RECORD_MAX_BYTES || keys.length > RECORD_MAX_SEEDS) {
      // Serialise once and key the cache on the same bytes we wrote, rather
      // than reading the store back to find out what is in it.
      const healed = JSON.stringify(out);
      try { localStorage.setItem(RECORD_KEY, healed); key = healed; } catch (err) { /* full */ }
    }
    /* FROZEN, BECAUSE THE CACHE HANDS OUT A REFERENCE.
     *
     * `records()` returned the cached object itself, `recordWin()` mutated
     * what it got back, and the cache was only cleared AFTER the write. So
     * one failed `setItem` left the cache holding a mutated object still
     * keyed to the old disk bytes, and the next successful win persisted the
     * phantom: two honest wins (wins=2 kills=16 seeds=6) ended on disk as
     * {"wins":3,"kills":23,"seeds":9}, with a row for a win that never
     * happened. Progress that was never earned is the same class of fault as
     * progress that is lost — the store stops describing what the player did.
     *
     * Freezing costs one pass per PARSE, not per call, and turns a silent
     * corruption into a throw at the line that does it. Mutating callers go
     * through `mutableRecords()`. */
    deepFreeze(out);
    host.recCache = { raw: key, value: out };
    return out;
  } catch (err) { return blank; }
}

/** A copy a caller may write to. One level of spread at each level is
 *  enough: nothing mutates a row in place, they are replaced whole. */
export function mutableRecords(host: RecordHost): { seeds: Record<string, RunRecord>; total: Totals } {
  const r = records(host);
  return { seeds: { ...r.seeds }, total: { ...r.total } };
}

/** The `RECORD_MAX_SEEDS` rows worth keeping: most recently set first, and
 *  a row with no `at` — every row written before `at` existed — sorts last
 *  but by its time, so an old personal best is not evicted ahead of a slow
 *  run from this morning. */
export function prune(seeds: Record<string, RunRecord>): Record<string, RunRecord> {
  const rank = Object.entries(seeds).sort((a, b) =>
    (b[1].at || 0) - (a[1].at || 0) || (a[1].frames || 0) - (b[1].frames || 0));
  const out: Record<string, RunRecord> = {};
  for (const [k, v] of rank.slice(0, RECORD_MAX_SEEDS)) out[k] = v;
  return out;
}

/** Would loadSave actually accept this seed's slot? The picker must not
 *  advertise a run the loader will refuse and then overwrite. */
export function canLoad(seed) {
  try {
    const d = peekSave(seed);
    /* NORMALISED ON BOTH SIDES, like `peekSave` does.
     *
     * This compared `d.seed === seed` raw while `peekSave` normalises both,
     * which is a FOURTH model of "does this slot belong to this seed" in the
     * method whose own docstring is about there having been four. Measured:
     * after `makeGame('Alpha').save()`, `peekSave('Alpha')` is truthy,
     * `canLoad('Alpha')` was **false**, and `loadSave(peekSave('Alpha'))`
     * returns true with the glim restored. Unreachable today only because
     * `listRuns()` happens to feed it an already-normalised seed. */
    return !!d && normaliseSeed(d.seed) === normaliseSeed(seed) && looksSane(d);
  } catch (err) { return false; }
}

/** Write the run down. Winning should leave a mark, or there is no reason to
 *  finish rather than wander. */
/* A RUN IS WRITTEN DOWN WHEN YOU LEAVE; THE WILDS ARE QUIETED WHEN THE THIRD
 * BEAST FALLS (round 52, three lenses in one voice, 2026-09-11). Under the
 * kill list a win was the only ending, so `recordWin` counted a WIN on every
 * call and kept the FEWEST frames as best. Under LS-379 a run ends when you
 * choose, and this kept counting: a hold beside the start screen's stone at
 * frame 46 was "1 quieted   best 0:00" on the title, a run that felled nothing
 * beat a run that felled three, and booting the seed again and holding the
 * stone banked it again because nothing in the save said the run was left.
 * Now a leave is a RUN, the third fall is a QUIETING, the seed's best is the
 * run that CHANGED the most (the record screen's own sum), and a run left is
 * left in the save. */
export function changedOf(rec: any) {
  return (rec.felled || 0) * 5 + (rec.setDown || 0) + (rec.burned || 0) + (rec.caches || 0);
}
export function recordWin(game, host: RecordHost, kind: 'run' | 'quieted' | 'again' = 'run') {
  try {
    const all = mutableRecords(host);
    all.seeds = all.seeds || {};
    all.total = all.total || {};
    const prev = all.seeds[game.seed];
    const rec = game.runRecord ? game.runRecord() : {};
    const run = { frames: game.runFrames, glim: game.state.glim, kills: game.state.kills, changed: changedOf(rec), felled: rec.felled || 0, at: Date.now() };
    if (kind === 'run' || kind === 'again') {
      if (!prev || run.changed > (prev.changed || 0)) all.seeds[game.seed] = run;
      if (!all.total.most || run.changed > all.total.most) all.total.most = run.changed;
    }
    if (kind === 'run') {
      all.total.runs = (all.total.runs || 0) + 1;
      /* ONE RUN, ONE BANKING (2026-09-17). These two were outside the branch, so a run that quieted the wilds banked
       * its kills and its seeds on the win AND again when it ended: the lifetime records read double for anyone who
       * finished. The run branch is the one that closes a run, so it is the one that banks its totals. */
      all.total.kills = (all.total.kills || 0) + game.state.kills;
      all.total.seeds = (all.total.seeds || 0) + game.state.seeds;
    } else if (kind === 'quieted') {
      /* ...AND ONLY A QUIETING BANKS A QUIETING (2026-09-20). `again` -- a run continued and left a second time --
       * was added beside `run` above and fell through this bare `else`, so every later hold of the stone read as
       * another world quieted on the title screen. */
      all.total.wins = (all.total.wins || 0) + 1;
    }
    /* Bound on the way in as well as on the way out, so the store cannot
     * grow past the cap in the first place — `records()` self-healing is for
     * a blob that arrived some other way, not the normal path. The seed just
     * won carries the newest `at`, so pruning can never drop it. */
    if (Object.keys(all.seeds).length > RECORD_MAX_SEEDS) all.seeds = prune(all.seeds);
    /* Before the write, not after — the cache must never be newer than the
     * disk. This is NOT what fixes the phantom win: `mutableRecords()` is,
     * and reverting the clone turns the check red while reverting this
     * ordering does not, because a copy is not the cached object whichever
     * order the two lines run in. It is here so the invariant holds locally
     * rather than by reaching two methods away, and it is written down as
     * unproven so nobody later reads it as the guard it is not. */
    host.recCache = null;
    localStorage.setItem(RECORD_KEY, JSON.stringify(all));
    game.lastRun = run;
    game.beatRecord = !!(prev && run.changed > (prev.changed || 0));
    game.previousBest = prev ? (prev.changed || 0) : null;   // the most this seed was changed before this run
  } catch (err) { /* records are a nicety */ }
}

/** THE STONES REMEMBER (LS-294, decided 2026-09-13): a beast felled on any run is written to the records, so a stone
 *  that names it on a later world can show its likeness. A count per kind; written the way a win is. */
export function recordFelled(host: RecordHost, kind: string) {
  try {
    const all = mutableRecords(host);
    all.total = all.total || {};
    const f = all.total.felled || {};
    f[kind] = (f[kind] || 0) + 1;
    all.total.felled = f;
    host.recCache = null;
    localStorage.setItem(RECORD_KEY, JSON.stringify(all));
  } catch (err) { /* records are a nicety */ }
}
/** Has this beast kind ever fallen, on any run? */
export function everFelled(host: RecordHost, kind: string): boolean {
  try { const t = records(host).total; return !!(t && t.felled && t.felled[kind] > 0); } catch (err) { return false; }
}

/** Forget one run on purpose. The only deletion path in the game, and it
 *  needs a confirm in the UI before it is called. */
export function forgetRun(seed) {
  try { localStorage.removeItem(saveKeyFor(seed)); } catch (err) { /* ignore */ }
}

/** Every run this browser remembers, newest first. */
export function listRuns() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(SAVE_PREFIX)) continue;
      try {
        const d = JSON.parse(localStorage.getItem(k));
        if (!d || typeof d.seed !== 'string') continue;
        // the key must agree with the payload, or the slot is a ghost that
        // the picker lists and peekSave can never find
        if (k !== saveKeyFor(d.seed)) continue;
        out.push({
          seed: d.seed,
          beasts: (d.state && Array.isArray(d.state.beastsSlain)) ? d.state.beastsSlain.length : 0,
          at: d.at || 0,
          ok: !!(d.v === 2 && d.state && d.area),
        });
      } catch (err) { /* skip an unreadable slot */ }
    }
  } catch (err) { /* storage unavailable */ }
  return out.sort((a, b) => b.at - a.at);
}

/* A LOADER THAT THROWS IS A LOADER THAT LOSES RUNS.
 *
 * `loadSave` returning FALSE has a careful path behind it — the caller
 * quarantines the slot and nothing is overwritten. `loadSave` THROWING had
 * no path at all: `boot()` and `retry()` happen to wrap it, `newWorld()`
 * does not, and by the time anything throws the loader has already called
 * `reset(data.seed)` and adopted the slot's `writeSeq`.
 *
 * So the two outcomes are one outcome now. Anything that goes wrong in here
 * is a refusal, and a refusal is a thing this project already knows how to
 * survive. */
export function loadSave(game, data) {
  try {
    return game.loadSaveUnsafe(data);
  } catch (err) {
    /* THE RUN IN MEMORY IS NOT UNTOUCHED, and this used to say it was.
     *
     * `loadSaveUnsafe` calls `reset(data.seed)` before anything can throw,
     * so a refused load leaves the session pointed at the OTHER seed with a
     * fresh state. Staged: a live run on `runA` holding
     * {glim:400, seeds:2, maxHp:22, frames:18466} against a damaged slot for
     * `runB` came back {seed:'runb', glim:1, seeds:0, maxHp:10, frames:0}.
     *
     * Nothing is lost ON DISK, which is what matters and why this is a
     * comment rather than a defect — but the guarantee is held by
     * `main.ts`'s `game.save()` on the way out of `newWorld`, not by this
     * function being non-destructive. Saying so here is the difference
     * between a fact and a hope. */
    return false;
  }
}

export function loadSaveUnsafe(game, data) {
  if (!game.saveLooksSane(data)) return false;
  data = structuredClone(data);   // a load owns its copy: the run mutated the caller's blob, so a second load of the same object began from the first run's state (the robustness lens, round 42)
  // Reset builds the world immediately, so choose its saved layout before seeding props.
  game.reset(data.seed, data.state?.forgeLayout === 2 ? 2 : data.state?.forgeLayout === 1 ? 1 : 0);
  // Remember which write we are continuing from, so save() can tell whether
  // another tab has moved this seed on since.
  game.saveSeq = Number.isFinite(data.writeSeq) ? data.writeSeq : 0;
  // loading a slot is taking ownership of it too
  if (game.owned) game.owned.set(game.seed, game.saveSeq);
  // NOT Object.assign: it fires the `__proto__` setter, so a crafted save
  // could replace `state`'s prototype — 87 of 5,600 loaded and were corrupt.
  // Own keys only, defined rather than set.
  /* ...AND NOTHING BIGGER THAN STATE EVER IS.
   *
   * This copied EVERY own key of `data.state`, and `save()` writes `state`
   * straight back — so one crafted field carried whatever it liked through
   * a round trip. A reviewer put 4 MB in `state.junk`; the loader accepted
   * it and `save()` wrote it out, past the browser quota that latches
   * `progress is not saving` for that seed forever.
   *
   * A KEY whitelist was the first attempt and it was the wrong instrument:
   * `openingLoot` is defined by `sanitiseState`, which runs AFTER this
   * loop, so the glade re-granted its glim on every reload — one of this
   * suite's own checks caught it immediately. The bomb is not about which
   * key it arrives under, it is about SIZE, so that is what is refused.
   * Every genuine field here is a number, a boolean, a short string or a
   * small array of them. */
  /* DEPTH IS A SIZE TOO.
   *
   * This bounded array LENGTH and string LENGTH and stopped there, so
   * `[[[[...]]]]` — a ~6 KB slot, 2,741 deep — blew the stack. The throw
   * escaped `newWorld`, which had no try, AFTER `loadSave` had already run
   * `reset(data.seed)` and taken ownership of the slot: the session was left
   * on a fresh empty world that PASSED the overwrite guard, and one press of
   * START wrote 493 bytes over a run holding 400 glim and 9,999 frames.
   *
   * Four is deeper than anything the game writes into `state` (a flat list
   * of numbers is depth 1) and shallower than any stack. */
  const sane = (v, depth = 0) => {
    if (v === null || typeof v === 'number' || typeof v === 'boolean') return true;
    if (typeof v === 'string') return v.length <= 64;
    if (Array.isArray(v)) {
      /* A FLAT LIST OF KEYS IS NOT A SIZE BOMB (2026-09-17). Sixty-four was the bound for every array, and `lootTaken`
       * outgrows it in any full run -- a run that takes the world's caches (up to forty-five keys) and fells three
       * beasts (thirteen hoard keys each) writes eighty-odd. Past sixty-five the whole list was dropped and the fresh
       * game's empty one stood: every cache in the world became takeable again, every beast's hoard re-dropped on
       * every load, and the end screen's cache tally read zero for a run that had opened fifty. The bomb this guard
       * exists to stop is DEPTH and bytes, so a one-level list of primitives gets a generous count and everything
       * nested keeps the old bound. */
      const flat = v.every((x) => x === null || typeof x === 'number' || typeof x === 'boolean' || (typeof x === 'string' && x.length <= 64));
      if (flat) return v.length <= 4096;
      return depth < 4 && v.length <= 64 && v.every((x) => sane(x, depth + 1));
    }
    /* PLAIN OBJECTS TOO (LS-225): the bag is an object of stacks and a
     * beast's record is an object with a home pair in it. Without this both
     * were silently dropped on load -- a run reloaded with an empty bag and
     * beasts made afresh, which the save checks caught. Same bounds as an
     * array, no prototype keys, values sane at one more level. */
    if (typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
      const keys = Object.keys(v);
      return depth < 4 && keys.length <= 64 && keys.every((k) => k !== '__proto__' && k !== 'constructor' && sane(v[k], depth + 1));
    }
    return false;
  };
  for (const k of Object.keys(data.state)) {
    if (k === '__proto__' || !sane(data.state[k])) continue;
    Object.defineProperty(game.state, k, {
      value: data.state[k], writable: true, enumerable: true, configurable: true,
    });
  }
  if (!data.state.bag) game.state.bag = null;   // a save from before the bag: the fresh game's own bag would hide the flags from `migrate`
  migrate(game.state);   // an old save's tool flags become stacks in the bag (LS-225)
  /* THE RUN'S CLOCK BEFORE THE CLAMPS THAT READ IT (2026-09-17): `sanitiseState` bounds the draught (and the timers
   * below it) by `game.runFrames + 1200`, and `runFrames` was restored eighty lines further down -- so a save loaded
   * into a fresh game clamped every window against nought and a legitimate draught drunk at frame fifty thousand came
   * back expired. */
  game.runFrames = Number.isFinite(data.runFrames) ? data.runFrames : 0;
  game.sanitiseState();
  /* `visited` GETS THE SAME FILTER AS `charted`, and the comment three
   * lines up already claimed it did.
   *
   * It was the one collection with no element filter and no bound, and it
   * round-trips: `save()` writes back whatever `loadSave` accepted. A
   * crafted save carrying 400,000 keys came back as 400,001 in memory and
   * 5,489,415 bytes written — past the localStorage quota on every
   * mainstream browser, which latches `storageFailed` for that seed
   * permanently. `charted`, with the filter, kept 0 of the same payload.
   *
   * That is the progress-loss failure mode wearing a different hat: not a
   * run overwritten, a run that can never be written again. */
  /* CANONICAL, AND BOUNDED. A world has WORLD_W * WORLD_H screens and cannot
   * have visited more of them than that — a collection larger than the world
   * is not a save, it is a payload. */
  game.visited = new Set((Array.isArray(data.visited) ? data.visited : [])
    .map((k) => coordKey(k, WORLD_W, WORLD_H)).filter(Boolean)
    .slice(0, WORLD_W * WORLD_H));
  game.quiet = new Map();
  if (Array.isArray(data.quiet)) {
    for (const pair of data.quiet) {
      if (!Array.isArray(pair) || typeof pair[0] !== 'string') continue;
      // clamped: a crafted save could otherwise hold a screen empty forever
      if (!Number.isFinite(pair[1])) continue;
      // ...and the KEY is clamped too, not just the value. This validated
      // only that the number was finite, so 200,000 crafted keys all
      // survived and wrote 2.7 MB straight back out — past a browser quota,
      // which latches `storageFailed` permanently for that seed.
      if (!QUIET_KEY_RE.test(pair[0])) continue;
      if (game.quiet.size >= WORLD_W * WORLD_H * 4) break;
      game.quiet.set(pair[0], Math.max(0, Math.min(2, Math.floor(pair[1]))));
    }
  }
  // ...and validate the elements, which is what `visited` and
  // `broken` all do. Nothing throws on a bad entry today because these are
  // only membership keys, but "harmless by luck" is not a load contract.
  game.charted = new Set((Array.isArray(data.charted) ? data.charted : [])
    .map((k) => coordKey(k, WORLD_W, WORLD_H)).filter(Boolean)
    .slice(0, WORLD_W * WORLD_H));
  // Derive completion from validated progress, including older saves without a
  // timestamp. Loading never banks another win; it only restores continued play.
  const lairs = game.beastLairs();
  game.aftermath = lairs.length > 0 && lairs.every(l => game.state.beastsSlain.includes(l.kind))
    ? Math.max(1, game.runFrames - 61) : 0;
  /* `broken` AND `quiet` GET THE KEY FILTER AND THE BOUND TOO.
   *
   * `visited` was hardened in round 17 and these two were not, and they
   * round-trip exactly the same way: `save()` writes back what `loadSave`
   * accepted. A reviewer crafted 200,000 keys into each — `broken` survived
   * all 200,000 and wrote 3,889,422 bytes, `quiet` all 200,000 and 2,689,422
   * — past a 5 MB quota, which latches `storageFailed` for that seed and
   * clears only on a good write that can now never happen.
   *
   * `broken` validated the tile strings INSIDE each set and never the area
   * key; `quiet` validated only that its value was finite. And the check
   * written for exactly this failure ("a crafted save cannot inflate the
   * slot past what a browser will hold") crafted `visited`, `charted` and
   * `caves` and asserted their sizes — it even computed the byte count and
   * interpolated it into the message without asserting it. A check named
   * after bytes has to fail on bytes. */
  if (game.systems) game.systems.restore(data.sim);
  /* `placed` IS READ THE WAY `broken` IS: a wild or hollow key, a bounded
   * number of screens, and inside each a coordinate string with a tile kind
   * that is one of the two a player can set down. Anything else is dropped. */
  game.placed = new Map((Array.isArray(data.placed) ? data.placed : [])
    .filter(e => Array.isArray(e) && typeof e[0] === 'string' && Array.isArray(e[1]))
    .filter(e => AREA_KEY_RE.test(e[0]))
    .slice(0, WORLD_W * WORLD_H)
    .map(([k, list]) => [k, new Map(list
      .filter(q => Array.isArray(q) && typeof q[0] === 'string' && /^\d{1,2},\d{1,2}$/.test(q[0]) && (q[1] === T.THORN || q[1] === T.ROCK || q[1] === T.DEBRIS || q[1] === T.WATER || q[1] === T.BONES || q[1] === T.ROD || q[1] === T.BRAZIER_LIT))   /* ...and the lightning rod a player sets (LS-381; it was dropped on every reload) and the found forges' lit braziers (2026-09-12) */   // the two a player sets down, and the two more a beast marks (LS-379)
      .slice(0, 192))]));
  game.broken = new Map((Array.isArray(data.broken) ? data.broken : [])
    .filter(e => Array.isArray(e) && typeof e[0] === 'string' && Array.isArray(e[1]))
    .filter(e => AREA_KEY_RE.test(e[0]))
    .slice(0, WORLD_W * WORLD_H * 8)
    /* THROUGH `coordKey`, AND CAPPED, like every other coordinate set here.
     *
     * This validated the DECODED number and then stored the RAW string, with
     * no length cap and no count cap — which is verbatim the defect
     * `coordKey`'s own docstring was written about: `'0003,4'` and `'3,4'`
     * are distinct Set members meaning one tile. A crafted save with 512 keys
     * holding 120 aliases each round-tripped and made `save()` write
     * **11,404,703 bytes**, 2.2x a 5MB origin quota. Past it, `storageFailed`
     * latches and stays latched, so that seed never saves again — which is
     * the one absolute rule in GOAL.md, broken by a string that parses to the
     * same tile as the one beside it. (11.4MB is a reviewer's craft; the
     * check below reproduces the class at **4,034,436 bytes** against a bar
     * of 200,000, which is the number this repo can re-measure.)
     *
     * The check named after this crafted `[k, ['1,1']]` — one short canonical
     * tile per key — so two rounds of hardening both went to the outer key
     * and never to the set behind it. Measured after the fix: 4,034,436 ->
     * 11,567 bytes, 120 tiles in the first Set -> 1, and an honest save is
     * unchanged at 491 bytes, because `game.ts:1053` writes `tx + ',' + ty`
     * and was always canonical. */
    .map(([k, arr]) => [k, new Set(arr
      .map((t) => coordKey(t, COLS, ROWS)).filter(Boolean)
      .slice(0, COLS * ROWS))]));
  /* now that the save's world exists, confirm the area it names does too —
   * and take only the four fields an area HAS. `{ ...data.area }` copied
   * anything, and `save()` writes the area back, so `area.junk` was a 3 MB
   * round trip through a save the loader accepted. */
  const a0 = data.area || {};
  /* ...and `level` is CLAMPED, not copied. The fix above closed the key set
   * and left the size of one of the four keys it keeps: `area.level` on a
   * `wild` area is never validated by `looksSane` (which checks it only when
   * `kind === 'hollow'`), so a 4,000,000-char string rode through and
   * `save()` wrote 4,000,531 bytes. It also let an accepted load leave a
   * typed-number field holding an object, on a session the game then runs —
   * harmless only because nothing indexes `hollows[level]` unless the kind is
   * `hollow`, and this file's own comment three lines up calls that sort of
   * luck "not a load contract". Measured after: 4,000,531 -> 530 bytes. */
  /* a save left in a hollow or a cave before LS-226 wakes at the start: there are no such places now */
  game.area = a0.kind === 'wild' ? { kind: 'wild', x: a0.x, y: a0.y, level: 0 } : { kind: 'wild', x: game.world.start.x, y: game.world.start.y, level: 0 };
  game.loadArea();
  /* ...AND THE PLAYER'S DROPS GO BACK ON THE GROUND, after `loadArea` has laid
   * out the world's own. Bounded and validated like every other list here: a
   * crafted save may not inflate the slot or place a thing off the map. */
  {
    const maxX = WORLD_W * PW, maxY = WORLD_H * PH;
    for (const d of (Array.isArray(data.dropped) ? data.dropped : []).slice(0, 200)) {
      if (!d || typeof d.kind !== 'string' || !ITEMS[d.kind]) continue;
      if (!Number.isFinite(d.x) || !Number.isFinite(d.y)) continue;
      const it = new Item(d.kind, Math.max(0, Math.min(maxX, d.x)), Math.max(0, Math.min(maxY, d.y)), true, { dropped: true });
      if (Number.isFinite(d.dur)) it.dur = d.dur;
      /* a thing laid on a forge's rock goes back to being the fire's: it cannot be picked up and the forge reads it */
      /* `offW` is the rock's world tile (LS-511); an older save's `off` was the rock's tile inside the drop's own screen */
      const rock = Array.isArray(d.offW) ? d.offW : Array.isArray(d.off) ? [d.off[0] + Math.floor(it.x / PW) * COLS, d.off[1] + Math.floor(it.y / PH) * ROWS] : null;
      if (rock && Number.isFinite(rock[0]) && Number.isFinite(rock[1])) { (it as any).offered = true; (it as any).forgeTx = rock[0]; (it as any).forgeTy = rock[1]; if (d.seeded === 1) (it as any).seeded = true; }
      /* A DROP IS FILED UNDER ITS OWN SCREEN (2026-09-17). Every restored drop was pushed into `game.items`, which is the
       * screen the load happened on -- so a thing set down three screens away came back underfoot at the checkpoint, and
       * walking to where it was left found nothing. The stash is what holds a screen's things while you are elsewhere
       * (`streamWorld` wakes it when the body comes near), so a drop that belongs elsewhere goes there. */
      const dsx = Math.floor(it.x / PW), dsy = Math.floor(it.y / PH);
      if (game.area && (dsx !== game.area.x || dsy !== game.area.y)) {
        const k = dsx + ',' + dsy; const had = game.itemStash.get(k) || []; had.push(it); game.itemStash.set(k, had);
      } else game.items.push(it);
    }
  }
  if (data.player && game.area.kind === (data.area && data.area.kind)) {
    /* A SAVE FROM BEFORE THE WILDS BECAME ONE SPACE IS STILL A RUN.
     *
     * Without `space` the numbers are screen-local, so they are read as an
     * offset INTO the screen the save also names — which reconstructs exactly
     * where the player stood. With it they are already world coordinates and
     * are taken as they are. Either way the clamp is to the space the numbers
     * are in: clamping a world position to one screen's width is what would
     * drop a player four screens from where they saved. */
    const worldSpace = data.player.space === 'world';
    const ox = !worldSpace ? game.area.x * PW : 0;
    const oy = !worldSpace ? game.area.y * PH : 0;
    const maxX = WORLD_W * PW - game.player.w;
    const maxY = WORLD_H * PH - game.player.h;
    game.player.x = Math.max(0, Math.min(maxX, data.player.x + ox));
    game.player.y = Math.max(0, Math.min(maxY, data.player.y + oy));
    game.player.dir = data.player.dir;
  }
  game.player.recentre();
  /* ABOARD, THE HULL'S PLACE IS THE BODY'S (2026-09-17): the sea is not standable, so the rescue below would take a
   * sailing body ashore on whatever island it could reach. The hull is where it was; the body sits in it. */
  if (data.aboard && game.state && game.state.boat) {
    const b = game.state.boat, p = game.player;
    game.aboard = true;
    p.x = b.x - p.w / 2; p.y = b.y - p.h / 2 - (b.kind === 'boat' ? 6 : 3);
    p.recentre();
  } else game.freeThePlayer();
  /* what ails the body, put back through the same door that puts it there in play, and bounded by its own window */
  if (Array.isArray(data.ail)) {
    const CAP = 3600;
    for (const row of data.ail.slice(0, 16)) {
      if (!Array.isArray(row) || typeof row[0] !== 'string' || !AFFLICTION_NAMES.includes(row[0])) continue;
      const left = Math.max(0, Math.min(CAP, Math.round(+row[1] || 0)));
      if (!left) continue;
      const a = afflict(game.player, row[0], left);
      if (a && Number.isFinite(+row[2])) a.t = Math.max(0, Math.min(CAP, Math.round(+row[2])));
      /* A SAVE FROM BEFORE THIS COLUMN EXISTED keeps its bites rather than its clock: the beat restarts at the
       * first bite, which costs the player nothing and cannot double-charge them. */
      if (a && Number.isFinite(+row[3])) a.bit = Math.max(0, Math.min(999, Math.round(+row[3])));
      else if (a) a.t = 0;
    }
  }
  if (Array.isArray(data.chops)) {
    game.treeChops = new Map();
    for (const row of data.chops.slice(0, 256)) {
      if (!Array.isArray(row) || typeof row[0] !== 'string' || !/^[-0-9a-zA-Z|,]{1,24}$/.test(row[0])) continue;
      const n = Math.max(0, Math.min(99, Math.round(+row[1] || 0)));
      if (n) game.treeChops.set(row[0], n);
    }
  }
  game.mode = 'title';
  game.resumed = true;
  return true;
}

/** Clamp anything the save could carry into a legal range. */
export function sanitiseState(game) {
  const st = game.state;
  /* THE BEASTS' RECORDS (LS-230): a save says what it likes; a record is kept
   * only if it is whole -- a kind the game has, a home pair, finite numbers,
   * a boolean life -- else the whole set is remade from the lairs on first
   * need (`beasts()` rebuilds a null), the lit marks kept. */
  const kinds = game.beastLairs ? new Set(game.beastLairs().map((l) => l.kind)) : null;
  sanitiseBag(st);   // every stack a known kind within its cap, the cursor and pick in range
  /* what each kind thinks of you (LS-522): known kinds, finite, within twenty either way */
  { const r = st.rep && typeof st.rep === 'object' && !Array.isArray(st.rep) ? st.rep : {}; const clean: Record<string, number> = {}; for (const k of Object.keys(r)) if (ENEMY_DEFS[k] && Number.isFinite(r[k])) clean[k] = Math.max(-20, Math.min(20, r[k])); st.rep = clean; }
  if (!Array.isArray(st.beastsSlain)) st.beastsSlain = [];
  // only the lairs' own kinds, once each: three strange names plus one real kill was a win
  st.beastsSlain = [...new Set(st.beastsSlain.filter((k) => typeof k === 'string' && (!kinds || kinds.has(k))))];
  if (st.beasts !== null && st.beasts !== undefined) {
    const whole = Array.isArray(st.beasts) && st.beasts.length === 3 && st.beasts.every((b) => b && typeof b === 'object' && typeof b.kind === 'string' && (!kinds || kinds.has(b.kind))
      && Array.isArray(b.home) && b.home.length === 2 && Number.isInteger(b.home[0]) && Number.isInteger(b.home[1])
      && [b.x, b.y, b.hp, b.tx, b.ty].every(Number.isFinite) && typeof b.alive === 'boolean');
    if (!whole) st.beasts = null;
    /* A RECORD WITHIN ITS BEAST (the robustness lens, 2026-09-05): `isFinite` let hp 1e300 through (a body that
     * cannot die: 1e300 - 2 is 1e300) and hp 0 with alive true (a standing corpse). Life is one to the kind's own. */
    else for (const b of st.beasts) { const slain = st.beastsSlain.length; const cap = Math.round((ENEMY_DEFS[b.kind] ? ENEMY_DEFS[b.kind].hp : 50) * Math.pow(BEAST_GROWTH, Math.min(3, slain)));   /* a beast grown by the kills before it keeps its growth */ b.hp = Math.max(0, Math.min(cap, Math.round(b.hp))); b.alive = b.alive && b.hp > 0; if (b.alive && b.hp < 1) b.hp = 1; if (Number.isFinite(b.max)) b.max = Math.max(1, Math.min(cap, Math.round(b.max))); else delete b.max; if ('heard' in b) b.heard = b.heard === true; if ('dead' in b) delete b.dead; delete b.shed; }   // the whole record typed (the robustness lens, round 44): NaN in heard loaded and was written back; shed is derived when a body forms
  }
  if (!Array.isArray(st.lootTaken)) st.lootTaken = [];
  /* WHAT THE BEASTS LEFT, kept so a reload cannot delete it (the robustness lens, round 51). A corrupt entry is
   * dropped rather than trusted: a bad screen or a bad kind would put an item nowhere, or an item that is nothing. */
  // Hull position, momentum, heading and mooring survive independently; legacy saves keep their shape.
  const hull = (b) => b && typeof b === 'object' && (b.kind === 'boat' || b.kind === 'raft') && Number.isFinite(b.x) && Number.isFinite(b.y)
    ? { kind: b.kind, x: b.x, y: b.y, vx: Number.isFinite(b.vx) ? b.vx : 0, vy: Number.isFinite(b.vy) ? b.vy : 0, heading: Number.isInteger(b.heading) ? ((b.heading % 8) + 8) % 8 : 2, ...(b.moored ? { moored: true } : {}) } : null;
  if (st.boat !== undefined) st.boat = hull(st.boat);
  // Missing collection stays missing so legacy single-hull saves round-trip unchanged.
  if (st.otherBoats !== undefined) st.otherBoats = Array.isArray(st.otherBoats)
    ? st.otherBoats.map(hull).filter(Boolean).slice(0, MAX_HULLS - (st.boat ? 1 : 0)) : [];
  if (st.forgeSeeded !== undefined) st.forgeSeeded = Array.isArray(st.forgeSeeded) ? st.forgeSeeded.filter((k: any) => typeof k === 'string') : [];   // the found forges whose laid things were set out (2026-09-12); a save without the key keeps none, so loading changes nothing
  if (st.forgeLayout !== undefined) st.forgeLayout = st.forgeLayout === 2 ? 2 : st.forgeLayout === 1 ? 1 : 0;   // as its neighbours are (2026-09-20): a save without the key gained a 0 merely by being loaded
  st.hoard = Array.isArray(st.hoard)
    ? st.hoard.filter((h: any) => h && typeof h.kind === 'string' && typeof h.key === 'string'
        && [h.x, h.y, h.px, h.py].every(Number.isFinite)).slice(0, 64)
    : [];
  sanitiseGear(st);   // what is worn: a piece in its own slot, one of it (LS-239)
  st.maxHp = Math.max(2, Math.min(MAX_HP_CAP, Math.round(st.maxHp)));
  /* AND A LOADED RUN HAS AT LEAST ONE POINT OF VIGOR (2026-09-17). `Player.hurt` returns at once when hp is
   * nought and is the only caller of onDeath, so a checkpoint written at nought -- which pressing R on the death
   * screen used to do -- reloads into a run that cannot be hurt and cannot end. Saves like that are already on
   * players' disks, so the load lifts them to one rather than the write alone being fixed. */
  st.hp = Math.max(0, Math.min(st.maxHp, Math.round(st.hp)));
  if (!(st.hp > 0)) st.hp = 1;
  st.maxPods = Math.max(1, Math.min(MAX_PODS_CAP, Math.round(st.maxPods)));
  st.pods = Math.max(0, Math.min(st.maxPods, Math.round(st.pods)));
  st.glim = Math.max(0, Math.min(999, Math.round(st.glim)));
  // `seeds` is a dead number kept for the records and the old-save shape (LS-226)
  st.seeds = Math.max(0, Math.min(3, Math.round(st.seeds) || 0));
  delete st.sigils; delete st.cleared;
  st.kills = Math.max(0, Math.round(st.kills) || 0);
  st.hasPods = !!st.hasPods; st.hasTide = !!st.hasTide; st.hasEmber = !!st.hasEmber;
  st.rattle = !!st.rattle || !!st.hasPods;   // an older save that holds pods held the Rattle (2026-09-18)
  /* The slot is untrusted input like everything else. This claimed to stop a
   * save "naming a tool you do not carry", and it does not — it only checks
   * the name is a tool at all, so `{tool:'ember', hasEmber:false}` survives
   * the load intact. The behaviour is correct anyway because `equipped()`
   * filters against `ownedTools()`, so the fix is to say what this line
   * does rather than to add a second owner check that would then be a second
   * model of ownership. */
  if (!TOOLS.includes(st.tool)) st.tool = TOOLS[0];
  st.emberCool = Math.max(0, Math.min(EMBER_COOL, +st.emberCool || 0));
  // beam was the one field left unclamped: a NaN in a save makes
  // Math.min(1, NaN + 1/150) stay NaN and `NaN >= 1` false forever, so the
  // throw is permanently and silently gone.
  st.beam = Math.max(0, Math.min(1, +st.beam || 0));
  st.openingLoot = !!st.openingLoot;
  // one lungful, or none: a save cannot carry half of one
  st.lungFull = !!st.lungFull;
  /* THE CONSUMABLES' TWO NUMBERS (LS-335, fourth batch). `pitch` is blows left
   * on the coating, three at most; `draught` is a FRAME the run has to pass,
   * so it is bounded by the run's own clock plus its length -- a save claiming
   * 1e308 would be twenty seconds that never end. `pitchSwing` is a swing id
   * and only ever compared for equality, so a wild value costs one blow. */
  st.pitch = Math.max(0, Math.min(3, Math.round(st.pitch) || 0));
  st.setDown = Math.max(0, Math.min(99999, Math.round(st.setDown) || 0));   // a tally (LS-379)
  st.pitchSwing = Math.max(0, Math.min(1e9, Math.round(st.pitchSwing) || 0));
  st.draught = Math.max(0, Math.min((+game.runFrames || 0) + 1200, Math.round(st.draught) || 0));
  /* ...AND EVERY OTHER WINDOW A CONSUMABLE OPENS (2026-09-17). The draught was bounded and the five that came after it
   * were not: a save claiming 1e308 was an endless haste, sight, warmth, dryness or gust. Same rule -- a frame the run
   * has to pass, at most its own clock plus the window's length -- and `stone` is blows left, like `pitch`. */
  /* ...AND A CLAMP DOES NOT INVENT THE FIELD IT CLAMPS (2026-09-18). Writing these unconditionally gave every save
   * that had never held a draught a `haste: 0`, `sight: 0`, `stone: 0` and a `gust: null` the moment it was loaded,
   * so loading a save and saving it again produced a DIFFERENT file -- which fuzz.ts asks about by name, and which
   * makes every "did this change?" comparison downstream a lie. A window that is not there stays not there. */
  for (const k of ['haste', 'sight', 'warm', 'dry']) if (st[k] !== undefined) st[k] = Math.max(0, Math.min((+game.runFrames || 0) + 1200, Math.round(st[k]) || 0));
  if (st.stone !== undefined) st.stone = Math.max(0, Math.min(10, Math.round(st.stone) || 0));
  if (st.gust && typeof st.gust === 'object') { const u = Math.round(st.gust.until) || 0; if (!(u > 0) || u > (+game.runFrames || 0) + 1200 || !Array.isArray(st.gust.dir) || st.gust.dir.length !== 2 || !st.gust.dir.every((n) => Number.isFinite(n))) st.gust = null; else st.gust = { dir: [+st.gust.dir[0], +st.gust.dir[1]], until: u }; }
  else if (st.gust !== undefined) st.gust = null;
  /* `reach`, `ward` AND `broke` ARE DROPPED, NOT CLAMPED (2026-09-06). All three were the shop's; nothing has
   * granted any of them since the pivot took it, so the game no longer reads them at all. Clamping them kept an old
   * save's copy alive and made a loaded run a different SHAPE from a fresh one -- which is the asymmetry the fuzzer
   * caught as save -> load -> save not returning the same file. Deleting them here is what makes the two agree: a
   * save written in the shop's day still carries them, and the check for this proved it, because the loader copies
   * the state it is given wholesale rather than by a whitelist. */
  delete st.reach; delete st.ward; delete st.broke;
  /* `kills` WAS THE LAST NUMBER IN THE STATE NOBODY BOUNDED (the save fuzzer, 2026-09-06). Setting every numeric
   * field in a save to 1e308 in turn and loading it, ten of the eleven came back clamped or refused -- hp to 10,
   * glim to 999, maxHp into 2..26, the position back inside the world -- and this one came back as 1e308 and was
   * carried into the run, onto the win screen's `felled` line, and into the records file the next save writes. It
   * is a tally of creatures felled, so a whole number and a sane ceiling is the whole of it. */
  st.kills = Math.max(0, Math.min(99999, Math.round(st.kills) || 0));
}

/** Explicitly clears only this seed's slot. Completing a hunt or switching
 * worlds no longer discards it. */
export function clearSave(game) {
  try { localStorage.removeItem(saveKeyFor(game.seed)); } catch (err) { /* ignore */ }
}
